import { CircleDot, Globe, Lock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  FormLayout,
  FormSection,
  RichTextEditor,
  TagInput,
} from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import {
  HelpArticleStatusInput,
  type HelpArticle,
  type HelpVisibility,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { slugify } from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  stripHtml,
  truncateWords,
  useSeoStoreContext,
} from '@/features/seo';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useHelpCategories } from '../hooks/use-help-categories';
import { articleState, draftState, type HelpArticleState } from '../schemas/article-state';
import {
  HANDLE,
  helpArticleFormSchema,
  KEYWORD_MAX_LENGTH,
  MAX_KEYWORDS,
  MAX_POSITION,
  type HelpArticleFormInput,
  type HelpArticleFormOutput,
} from '../schemas/help-article-form-schema';

import { VisibilityRadioGroup } from './visibility-radio-group';

export const HELP_ARTICLE_FORM_ID = 'help-article-form';

const NO_CATEGORY = '__none__';
const ALL_LOCALES = '__all__';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'title',
  'handle',
  'excerpt',
  'bodyHtml',
  'categoryId',
  'keywords',
  'locale',
  'position',
  'status',
  'visibility',
  'seo.title',
  'seo.description',
  'seo.canonical',
  'seo.noIndex',
]);
// `keywords.2` from the backend lands on the keyword input as a whole.
const mapField = (field: string) => (field.startsWith('keywords.') ? 'keywords' : field);

const STATE_ICON = { live: Globe, internal: Lock, draft: CircleDot } as const;

export interface HelpArticleFormProps {
  storeId: string;
  mode: 'create' | 'edit';
  /** The saved article in edit mode, for the rename hint, the live state and the saved SEO. */
  article: HelpArticle | null;
  defaultValues: HelpArticleFormInput;
  /** Locales an article may be pinned to; "All languages" is always offered. */
  locales: readonly string[];
  isSubmitting: boolean;
  onSubmit: (values: HelpArticleFormOutput) => Promise<unknown>;
  /** Extra cards under the SEO preview (the feedback list in edit mode). */
  children?: ReactNode;
  dangerZone?: ReactNode;
}

function SavedState({ article }: { article: HelpArticle }) {
  const { t } = useTranslation('helpCenter');
  const state = articleState(article);
  const Icon = STATE_ICON[state];
  return (
    <p className="flex items-center gap-2 text-sm" data-testid="article-saved-state">
      <Icon
        className={state === 'live' ? 'text-success size-4' : 'text-muted-foreground size-4'}
        aria-hidden
      />
      {t(`savedState.${state}`)}
    </p>
  );
}

function StateHint({
  state,
  internalCategory,
}: {
  state: HelpArticleState;
  internalCategory: boolean;
}) {
  const { t } = useTranslation('helpCenter');
  const key =
    state === 'internal' && internalCategory
      ? 'stateAfterSave.internalCategory'
      : `stateAfterSave.${state}`;
  return (
    <p className="text-muted-foreground text-xs" role="status">
      {t(key)}
    </p>
  );
}

/**
 * Two columns: content, organisation and SEO inside the <form>; publishing (status, visibility,
 * save) in the side rail, bound to the same react-hook-form context through `form=`.
 */
export function HelpArticleForm({
  storeId,
  mode,
  article,
  defaultValues,
  locales,
  isSubmitting,
  onSubmit,
  children,
  dangerZone,
}: HelpArticleFormProps) {
  const { t } = useTranslation(['helpCenter', 'common']);
  const message = useErrorMessage();
  const form = useForm<HelpArticleFormInput, unknown, HelpArticleFormOutput>({
    resolver: useI18nResolver(helpArticleFormSchema),
    defaultValues,
  });
  const categories = useHelpCategories(storeId);
  const storeContext = useSeoStoreContext(storeId);
  const [handleTouched, setHandleTouched] = useState(mode === 'edit');

  const values = form.watch();
  const category =
    values.categoryId === ''
      ? null
      : (categories.data?.find((c) => c.id === values.categoryId) ??
        (article?.category?.id === values.categoryId ? article.category : null));
  const categoryVisibility: HelpVisibility | null = category?.visibility ?? null;
  const state = draftState(values.status, values.visibility, categoryVisibility);
  const handle = values.handle.trim();
  const renamed =
    mode === 'edit' && article !== null && handle !== article.handle && HANDLE.test(handle);
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;
  const localeOptions =
    values.locale !== '' && !locales.includes(values.locale)
      ? [...locales, values.locale]
      : locales;

  const live = storeContext
    ? resolveSeoPreview(
        {
          title: values.title,
          description:
            values.excerpt.trim() ||
            (values.bodyHtml ? truncateWords(stripHtml(values.bodyHtml), 300) : null),
          path: `/help/a/${handle}`,
          published: state === 'live',
          override: values.seo,
          image: null,
        },
        storeContext,
      )
    : null;

  const submit = form.handleSubmit(async (submitted) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(submitted);
      if (mode === 'edit') form.reset(submitted);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('helpCenter:sections.publishing')}>
              {article ? <SavedState article={article} /> : null}
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('helpCenter:fields.status')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                      >
                        {Object.values(HelpArticleStatusInput).map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`help-article-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`help-article-status-${status}`}>
                                {t(`helpCenter:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`helpCenter:statusHint.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('helpCenter:fields.visibility')}</FormLabel>
                    <FormControl>
                      <VisibilityRadioGroup
                        idPrefix="help-article-visibility"
                        value={field.value}
                        onChange={field.onChange}
                        hintKey="visibilityHint"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <StateHint state={state} internalCategory={categoryVisibility === 'internal'} />
              <Can permission="help.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={HELP_ARTICLE_FORM_ID}
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('helpCenter:create.submit')
                        : t('common:actions.save')}
                  </Button>
                  {mode === 'edit' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('helpCenter:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            {dangerZone}
          </>
        }
      >
        <form
          id={HELP_ARTICLE_FORM_ID}
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-6"
        >
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            title={t('helpCenter:sections.content')}
            description={t('helpCenter:sections.contentDescription')}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('helpCenter:fields.title')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus={mode === 'create'}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!handleTouched) {
                          form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('helpCenter:fields.handle')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono text-[13px]"
                      onChange={(e) => {
                        setHandleTouched(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      mode === 'create'
                        ? 'helpCenter:fields.handleHintCreate'
                        : 'helpCenter:fields.handleHint',
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {renamed ? (
              <Alert role="status">
                <AlertDescription>
                  {t('helpCenter:rename.article', {
                    from: `/help/a/${article.handle}`,
                    to: `/help/a/${handle}`,
                  })}
                </AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="excerpt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('helpCenter:fields.excerpt')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormDescription>{t('helpCenter:fields.excerptHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bodyHtml"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('helpCenter:fields.body')}</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value}
                      onChange={(html) => field.onChange(html ?? '')}
                      onBlur={field.onBlur}
                      disabled={field.disabled}
                    />
                  </FormControl>
                  <FormDescription>{t('helpCenter:fields.bodyHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('helpCenter:sections.organisation')}
            description={t('helpCenter:sections.organisationDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('helpCenter:fields.category')}</FormLabel>
                    <Select
                      value={field.value === '' ? NO_CATEGORY : field.value}
                      onValueChange={(value) => field.onChange(value === NO_CATEGORY ? '' : value)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NO_CATEGORY}>
                          {t('helpCenter:fields.noCategory')}
                        </SelectItem>
                        {(categories.data ?? []).map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.visibility === 'internal'
                              ? t('helpCenter:fields.internalCategory', { name: option.name })
                              : option.name}
                          </SelectItem>
                        ))}
                        {field.value !== '' &&
                        categories.data &&
                        !categories.data.some((c) => c.id === field.value) ? (
                          <SelectItem value={field.value}>
                            {article?.category?.id === field.value
                              ? article.category.name
                              : t('helpCenter:fields.unknownCategory')}
                          </SelectItem>
                        ) : null}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locale"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('helpCenter:fields.locale')}</FormLabel>
                    <Select
                      value={field.value === '' ? ALL_LOCALES : field.value}
                      onValueChange={(value) => field.onChange(value === ALL_LOCALES ? '' : value)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={ALL_LOCALES}>
                          {t('helpCenter:fields.allLocales')}
                        </SelectItem>
                        {localeOptions.map((locale) => (
                          <SelectItem key={locale} value={locale}>
                            {locale}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('helpCenter:fields.localeHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="keywords"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('helpCenter:fields.keywords')}</FormLabel>
                    <FormControl>
                      <TagInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        max={MAX_KEYWORDS}
                        maxLength={KEYWORD_MAX_LENGTH}
                        placeholder={t('helpCenter:fields.keywordsPlaceholder')}
                        removeLabel={(keyword) => t('helpCenter:fields.removeKeyword', { keyword })}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('helpCenter:fields.keywordsHint', {
                        max: MAX_KEYWORDS,
                        length: KEYWORD_MAX_LENGTH,
                      })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('helpCenter:fields.position')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={MAX_POSITION}
                        className="w-32"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('helpCenter:fields.articlePositionHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>

          <SeoOverrideFields />
          <SeoPreviewCard
            live={live}
            saved={article?.resolvedSeo ?? null}
            path={`/help/a/${handle}`}
          />
          {children}
        </form>
      </FormLayout>
    </Form>
  );
}
